import type { ApiResponse } from '@kangaroo/shared';
import {
  DriverStatus,
  type CreateDriverRequest,
  type CreateDriverResponse,
  type DriverListItem,
  type DriverDirectoryResponse,
  type DriverSummaryStats,
  type LinkDriverParentRequest,
  type ListDriversQuery,
  type PaginatedResponse,
  type UpdateDriverRequest,
} from '@kangaroo/shared';

import { api } from '../lib/api';

export async function fetchDrivers(query: ListDriversQuery = {}) {
  const response = await api.get<PaginatedResponse<DriverListItem>>('/drivers', { params: query });
  return response.data;
}

export async function fetchAllDrivers(
  query: Omit<ListDriversQuery, 'page' | 'perPage'> = {},
  includeKartAssignments = false,
  fetchDirectory: (
    query: Omit<ListDriversQuery, 'page' | 'perPage'>,
    includeKartAssignments: boolean,
  ) => Promise<DriverDirectoryResponse> = async (directoryQuery, includeAssignments) => {
    const response = await api.get<ApiResponse<DriverDirectoryResponse>>('/drivers/directory', {
      params: { ...directoryQuery, includeKartAssignments: includeAssignments },
    });
    return response.data.data;
  },
): Promise<DriverDirectoryResponse> {
  return fetchDirectory(query, includeKartAssignments);
}

export async function fetchDriver(driverId: string) {
  const response = await api.get<ApiResponse<DriverListItem>>(`/drivers/${driverId}`);
  return response.data.data;
}

export async function fetchDriverStats() {
  const response = await api.get<ApiResponse<DriverSummaryStats>>('/drivers/stats/summary');
  return response.data.data;
}

export async function createDriver(payload: CreateDriverRequest) {
  const response = await api.post<ApiResponse<CreateDriverResponse>>('/drivers', payload);
  return response.data.data;
}

export async function updateDriver(driverId: string, payload: UpdateDriverRequest) {
  const response = await api.patch<ApiResponse<DriverListItem>>(`/drivers/${driverId}`, payload);
  return response.data.data;
}

export async function uploadDriverAvatar(driverId: string, file: File) {
  const formData = new FormData();
  formData.append('avatar', file);
  const response = await api.post<ApiResponse<DriverListItem>>(`/drivers/${driverId}/avatar`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data.data;
}

export async function linkDriverParent(driverId: string, payload: LinkDriverParentRequest) {
  const response = await api.post<ApiResponse<DriverListItem>>(`/drivers/${driverId}/parents`, payload);
  return response.data.data;
}

export async function releaseDriver(driverId: string) {
  const response = await api.post<ApiResponse<DriverListItem>>(`/drivers/${driverId}/release`);
  return response.data.data;
}

export function driverStatusQuery(tab: 'All Drivers' | 'Active' | 'Inactive' | 'Graduated'): ListDriversQuery['status'] {
  switch (tab) {
    case 'Active':
      return DriverStatus.ACTIVE;
    case 'Inactive':
      return DriverStatus.INACTIVE;
    case 'Graduated':
      return DriverStatus.GRADUATED;
    default:
      return 'ALL';
  }
}

export function driverListKey(query: ListDriversQuery) {
  return ['drivers', query] as const;
}

export function allDriversKey(
  query: Omit<ListDriversQuery, 'page' | 'perPage'> = {},
  includeKartAssignments = false,
) {
  return ['drivers', 'directory', query, includeKartAssignments] as const;
}

export function driverDetailKey(driverId: string) {
  return ['drivers', driverId] as const;
}

export const driverStatsKey = ['drivers', 'stats'] as const;
